import type {
  CompanyProfileRecord,
  ContractCollectionRecord,
  ContractDetailRecord,
  ContractRecord,
  ContractStatus,
  CustomerSummaryRecord,
  DocumentProjectSummaryRecord
} from '@shared/types';

type SortDirection = 'asc' | 'desc';

export interface ContractTableRowViewModel {
  id: string;
  title: string;
  contractNumber: string;
  statusLabel: string;
  statusTone: 'neutral' | 'primary' | 'warning' | 'success' | 'info';
  customerName: string;
  projectName: string;
  issuerName: string;
  effectiveLabel: string;
  valueLabel: string;
}

export interface ContractSelectionItemViewModel {
  id: string;
  label: string;
  subtitle: string | null;
}

export interface ContractEditorViewModel {
  title: string;
  contractNumber: string;
  statusLabel: string;
  statusTone: ContractTableRowViewModel['statusTone'];
  customerName: string;
  projectName: string;
  issuerName: string;
  effectiveFromLabel: string;
  effectiveUntilLabel: string;
  totalValueLabel: string;
  signatureLabel: string;
  templateLabel: string;
  generatedLabel: string;
}

function getCustomer(summaryList: CustomerSummaryRecord[], customerId: string) {
  return summaryList.find((customer) => customer.id === customerId) ?? null;
}

function getProject(summaryList: DocumentProjectSummaryRecord[], projectId: string | null) {
  return projectId ? summaryList.find((project) => project.id === projectId) ?? null : null;
}

function getIssuer(summaryList: CompanyProfileRecord[], issuerCompanyProfileId: string) {
  return summaryList.find((profile) => profile.id === issuerCompanyProfileId) ?? null;
}

function contractStatusTone(status: ContractStatus): ContractTableRowViewModel['statusTone'] {
  switch (status) {
    case 'signed':
      return 'success';
    case 'ready_to_send':
      return 'primary';
    case 'sent':
      return 'info';
    case 'expired':
    case 'archived':
      return 'neutral';
    default:
      return 'warning';
  }
}

function formatShortDate(locale: string, value: string | null, fallback: string): string {
  if (!value) {
    return fallback;
  }

  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(new Date(value));
}

function formatCurrency(locale: string, value: number | null, currency: string, fallback: string): string {
  if (value === null) {
    return fallback;
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0
  }).format(value);
}

function withDirection(value: number, direction: SortDirection): number {
  return direction === 'asc' ? value : value * -1;
}

function sortContracts(contracts: ContractRecord[], sort: 'recent' | 'effective' | 'title', direction: SortDirection): ContractRecord[] {
  const nextContracts = [...contracts];

  switch (sort) {
    case 'effective':
      return nextContracts.sort((left, right) => {
        if (!left.effectiveUntil && !right.effectiveUntil) {
          return 0;
        }
        if (!left.effectiveUntil) {
          return 1;
        }
        if (!right.effectiveUntil) {
          return -1;
        }

        return withDirection(new Date(left.effectiveUntil).getTime() - new Date(right.effectiveUntil).getTime(), direction);
      });
    case 'title':
      return nextContracts.sort((left, right) => withDirection(left.title.localeCompare(right.title), direction));
    default:
      return nextContracts.sort((left, right) => withDirection(new Date(left.updatedAt).getTime() - new Date(right.updatedAt).getTime(), direction));
  }
}

export function selectContractsSummary(collection: ContractCollectionRecord) {
  return {
    activeCount: collection.contracts.filter((contract) => contract.status !== 'archived').length,
    archivedCount: collection.contracts.filter((contract) => contract.status === 'archived').length
  };
}

export function selectContractRows(
  collection: ContractCollectionRecord,
  options: {
    locale: string;
    statusFilter: 'all' | ContractStatus;
    customerFilter: 'all' | string;
    projectFilter: 'all' | string;
    sort: 'recent' | 'effective' | 'title';
    sortDirection?: SortDirection | undefined;
    t: (key: string, values?: Record<string, string | number>) => string;
  }
): ContractTableRowViewModel[] {
  return sortContracts(
    collection.contracts.filter((contract) => {
      const matchesStatus = options.statusFilter === 'all' || contract.status === options.statusFilter;
      const matchesCustomer = options.customerFilter === 'all' || contract.customerId === options.customerFilter;
      const matchesProject = options.projectFilter === 'all' || contract.projectId === options.projectFilter;
      return matchesStatus && matchesCustomer && matchesProject;
    }),
    options.sort,
    options.sortDirection ?? (options.sort === 'title' ? 'asc' : 'desc')
  ).map((contract) => {
    const customer = getCustomer(collection.customerSummaries, contract.customerId);
    const project = getProject(collection.projectSummaries, contract.projectId);
    const issuer = getIssuer(collection.companyProfiles, contract.issuerCompanyProfileId);

    return {
      id: contract.id,
      title: contract.title,
      contractNumber: contract.contractNumber,
      statusLabel: options.t(`enums.contractStatus.${contract.status}`),
      statusTone: contractStatusTone(contract.status),
      customerName: customer?.name ?? contract.customerSnapshot.displayName,
      projectName: project?.name ?? options.t('common.notSet'),
      issuerName: issuer?.name ?? contract.issuerSnapshot.displayName,
      effectiveLabel: contract.effectiveUntil
        ? options.t('contracts.table.effectiveUntil', {
            date: formatShortDate(options.locale, contract.effectiveUntil, options.t('common.notSet'))
          })
        : options.t('contracts.table.noEndDate'),
      valueLabel: formatCurrency(options.locale, contract.totalValue, contract.currency, options.t('common.notSet'))
    };
  });
}

export function selectContractCustomerOptions(collection: ContractCollectionRecord, t: (key: string) => string) {
  return [
    { value: 'all', label: t('contracts.filters.allCustomers') },
    ...collection.customerSummaries.map((customer) => ({
      value: customer.id,
      label: customer.name
    }))
  ];
}

export function selectContractProjectOptions(collection: ContractCollectionRecord, t: (key: string) => string) {
  return [
    { value: 'all', label: t('contracts.filters.allProjects') },
    ...collection.projectSummaries.map((project) => ({
      value: project.id,
      label: project.name
    }))
  ];
}

export function selectContractSelectionItems(collection: ContractCollectionRecord): ContractSelectionItemViewModel[] {
  return collection.contracts.map((contract) => ({
    id: contract.id,
    label: contract.title,
    subtitle: contract.contractNumber
  }));
}

export function selectContractEditorModel(
  detail: ContractDetailRecord,
  options: {
    locale: string;
    t: (key: string, values?: Record<string, string | number>) => string;
  }
): ContractEditorViewModel {
  const customer = getCustomer(detail.customerSummaries, detail.contract.customerId);
  const project = getProject(detail.projectSummaries, detail.contract.projectId);
  const issuer = getIssuer(detail.companyProfiles, detail.contract.issuerCompanyProfileId);

  return {
    title: detail.contract.title,
    contractNumber: detail.contract.contractNumber,
    statusLabel: options.t(`enums.contractStatus.${detail.contract.status}`),
    statusTone: contractStatusTone(detail.contract.status),
    customerName: customer?.name ?? detail.contract.customerSnapshot.displayName,
    projectName: project?.name ?? options.t('common.notSet'),
    issuerName: issuer?.name ?? detail.contract.issuerSnapshot.displayName,
    effectiveFromLabel: formatShortDate(options.locale, detail.contract.effectiveFrom, options.t('common.notSet')),
    effectiveUntilLabel: formatShortDate(options.locale, detail.contract.effectiveUntil, options.t('common.notSet')),
    totalValueLabel: formatCurrency(
      options.locale,
      detail.contract.totalValue,
      detail.contract.currency,
      options.t('common.notSet')
    ),
    signatureLabel: options.t(`enums.contractSignatureStatus.${detail.contract.signatureStatus}`),
    templateLabel: detail.contract.templateVersionLabel ?? options.t('common.notSet'),
    generatedLabel: formatShortDate(
      options.locale,
      detail.contract.generatedDocument?.generatedAt ?? detail.contract.generatedAt,
      options.t('common.notSet')
    )
  };
}
